import type { RecommendedProduct } from '@/lib/recommendations'

/**
 * FRIDGE-1501 (F2) — one name for a ToolProductCarousel's read, shared by the
 * server pages that load it ahead of time (lib/tool-carousel-prefetch.ts) and
 * the client carousel that looks it up (components/brand-kit/
 * tool-carousel-prefetch.tsx). Plain module: no client directive, so server
 * code can call it.
 */
export interface ToolCarouselRequest {
  collectionType: string
  fallbackCollectionType?: string
  limit?: number
  randomize?: boolean
  titleMatch?: string[]
}

export function toolCarouselKey(request: ToolCarouselRequest): string {
  return [
    request.collectionType,
    request.fallbackCollectionType ?? '',
    request.limit ?? 8,
    request.randomize ? 'random' : 'ranked',
    (request.titleMatch ?? []).join(','),
  ].join('|')
}

/** The read fields of a carousel's props, for the server loader's cache key. */
export function toolCarouselRequest({ collectionType, fallbackCollectionType, limit, randomize, titleMatch }: ToolCarouselRequest): ToolCarouselRequest {
  return { collectionType, fallbackCollectionType, limit, randomize, titleMatch }
}

/**
 * What a ToolProductCarousel card renders, and all a server page hands the
 * browser for it. The whole RecommendedProduct rows (specs_json with the long
 * description, every image, the price-range tags) made the hydration payload
 * of /tools/refrigerator-size-calculator 66 KB for 29 cards, and
 * html-render-gate measures the whole document (pull request 808, 10/15/2026).
 */
export type ToolCarouselProduct = Pick<RecommendedProduct, 'id' | 'brand' | 'title' | 'canonical_url' | 'images' | 'price' | 'originalPrice' | 'rating' | 'reviews'> & {
  specs_json?: { energy_star?: boolean }
}

export function toToolCarouselProduct(p: RecommendedProduct): ToolCarouselProduct {
  const out: ToolCarouselProduct = {
    id: p.id,
    brand: p.brand,
    title: p.title,
    canonical_url: p.canonical_url,
    // The card shows one image (getProductImage takes the first).
    images: p.images?.length ? [p.images[0]] : [],
  }
  if (typeof p.price === 'number') out.price = p.price
  if (typeof p.originalPrice === 'number') out.originalPrice = p.originalPrice
  if (typeof p.rating === 'number') out.rating = p.rating
  if (typeof p.reviews === 'number') out.reviews = p.reviews
  if (p.specs_json?.energy_star) out.specs_json = { energy_star: true }
  return out
}
